import { useState } from "react";
import { quantfolio, type JumpKind, type QpFlowOption } from "../../api";
import { qpRail } from "../../lib/qpRail";

// The two SSO-jump actions on the investor header:
//   - "Open in Quantfolio"   → pure SSO jump to the tenant home
//   - "Advise in Quantfolio" → pick a flow, create the session, deeplink in
// Both open the target in a new tab and pop the call-log rail so the advisor
// watches the integration work.
export function QpJumpActions({ investorId }: { investorId: number }) {
  const [flows, setFlows] = useState<QpFlowOption[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doJump(kind: JumpKind, flow?: QpFlowOption) {
    setBusy(true);
    setError(null);
    // Open a blank tab synchronously (inside the click) to dodge popup blockers;
    // set its location once the jump resolves.
    const win = window.open("", "_blank");
    try {
      const { jump } = await quantfolio.jump({
        investorId,
        kind,
        flowId: flow?.id,
        flowName: flow?.name,
      });
      qpRail.open();
      if (jump.jump_url) {
        if (win) win.location.href = jump.jump_url;
        else window.open(jump.jump_url, "_blank");
      } else if (win) {
        win.close();
      }
    } catch (e) {
      if (win) win.close();
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setPickerOpen(false);
    }
  }

  async function togglePicker() {
    const next = !pickerOpen;
    setPickerOpen(next);
    if (next && flows === null) {
      try {
        const d = await quantfolio.flows();
        setFlows(d.flows);
      } catch (e) {
        setError((e as Error).message);
      }
    }
  }

  return (
    <div className="qp-jump">
      {error && <div className="banner-error qp-jump-error">{error}</div>}
      <button
        type="button"
        className="btn btn-ghost"
        disabled={busy}
        onClick={() => doJump("sso")}
      >
        Open in Quantfolio
      </button>
      <div className="qp-picker-wrap">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={togglePicker}
          aria-expanded={pickerOpen}
        >
          Advise in Quantfolio ▾
        </button>
        {pickerOpen && (
          <div className="qp-picker">
            <div className="qp-picker-head muted">Start a flow</div>
            {flows === null ? (
              <div className="qp-picker-msg muted">Loading flows…</div>
            ) : flows.length === 0 ? (
              <div className="qp-picker-msg muted">No flows available</div>
            ) : (
              <ul>
                {flows.map((f) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => doJump("session", f)}
                      disabled={busy}
                    >
                      {f.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
